import DOMPurify from 'dompurify';
import { StatusCodes } from 'http-status-codes';
import { createLogger } from 'isomorphic-rslog';
import { REQUIRED_QUERY_PARAMS } from '@/lib/config';
import type { InternalAxiosRequestConfig } from 'axios';
import type { AnyObject, Maybe, ObjectSchema, ValidationError } from 'yup';

/**
 * Placeholder callback for APIs that accept a function but need no action.
 */
export function noop(): void {
  return undefined;
}

/**
 * Signals whether code is running under environments that mimic production.
 *
 * @returns `true` for staging/production deployments
 */
export function isTestingEnv() {
  return ['staging', 'production'].includes(process.env.ENVIRONMENT);
}

/**
 * Provides Next.js images with a Contentful-powered URL that respects format, width, and quality.
 *
 * @param props - Loader options received from Next.js
 * @returns A URL ready to plug into an `img`/`Image` component
 */
export function imageLoader({ src, width, quality }: ImageLoaderProps) {
  if (/^\/static\/assets.*/.test(src)) {
    return src;
  }
  const url = new URL(src);
  url.searchParams.set('fm', /\.svg$/.test(src) ? '' : 'webp');
  url.searchParams.set('w', width.toString());
  url.searchParams.set('q', (quality || 75).toString());
  url.protocol = 'https:';
  return url.href;
}

/**
 * Flags whether a path points to one of our supported image formats.
 *
 * @param pathName - Asset path or filename
 * @returns `true` when the asset can be rendered as an image
 */
export function isImage(pathName?: string) {
  if (!pathName) {
    return false;
  }

  return (
    pathName.endsWith('.png') ||
    pathName.endsWith('.jpg') ||
    pathName.endsWith('.jpeg') ||
    pathName.endsWith('.gif') ||
    pathName.endsWith('.svg') ||
    pathName.endsWith('.webp')
  );
}

/**
 * Runs Yup validation on an incoming payload and normalizes thrown errors.
 *
 * @param schema - Yup schema definition to apply
 * @param payload - Data to test against the schema
 * @returns A promise resolving with the original payload
 * @throws Array of validation messages when invalid
 */
export async function validateRequestPayload<
  T = Record<string, string>,
  S = ObjectSchema<AnyObject>,
>(schema: S, payload: T) {
  return (schema as NonNullable<Maybe<AnyObject>>)
    .validate(payload, { abortEarly: false })
    .catch((error: ValidationError) => {
      throw error.inner.reduce(
        (errors, err) => {
          const inner = Object(err);
          return { ...errors, [inner.path]: inner.message };
        },
        {} as Record<string, string>,
      );
    });
}

/**
 * Renders CMS-provided HTML as sanitized React markup using `dompurify`.
 *
 * @param html - HTML snippet to inject into the page
 * @returns A safe React wrapper or `null` when parsing fails
 */
export function parseHtml(html: string) {
  try {
    const safeHtml = DOMPurify.sanitize(html);
    const injectable = { __html: safeHtml };
    return <div dangerouslySetInnerHTML={injectable} />;
  } catch (error) {
    logger.error(error);
    return null;
  }
}

let shouldThrottle = false;

/**
 * Offers a lightweight throttle helper for DOM or event callbacks.
 *
 * @param callback - Function to run after the throttle delay
 * @param timeInMs - Interval window in milliseconds
 */
export function throttle(callback: () => void, timeInMs: number) {
  if (shouldThrottle || typeof timeInMs !== 'number' || isNaN(Number(timeInMs))) {
    return undefined;
  }
  shouldThrottle = true;
  setTimeout(() => {
    if (typeof callback === 'function') {
      callback();
    }
    shouldThrottle = false;
  }, timeInMs);
}

/**
 * Identifies values that should be treated as empty (`null`, `undefined`, `NaN`).
 *
 * @param value - Value to inspect
 */
export function isNullable(value: unknown) {
  return value === undefined || value === null || (typeof value === 'number' && isNaN(value));
}

/**
 * Builds an RFC 7617 Basic authorization header from credentials.
 *
 * @param username - HTTP auth user
 * @param password - HTTP auth password
 * @returns Fully formatted header or `null` when inputs are missing
 */
export function toToken(username: string, password: string) {
  const invalid = typeof username !== 'string' || typeof password !== 'string';
  const token = `Basic ${Buffer.from(`${username}:${password}`).toString('base64')}`;
  return invalid || !username || !password ? null : token;
}

/**
 * Decorates an access token for use as a Bearer authorization header.
 *
 * @param token - Raw token string
 * @returns Prefixed token or `null` when invalid
 */
export function toAuthToken(token: string) {
  return typeof token !== 'string' || !token ? null : `Bearer ${token}`;
}

/**
 * Normalizes ids or keys to `snake_case`.
 *
 * @param str - Source string or number
 * @returns The snake-cased value
 */
export function toSnake(str: string | number) {
  const invalid = !(typeof str === 'string' || (typeof str === 'number' && !isNaN(str)));
  return (
    invalid ? '' : (
      String(str)
        .replace(/^([a-zA-Z].*)(\s?[A-Z\s])/g, '$1_$2')
        .replace(/[\s_]+/g, '_')
        .replace(/^_/, '')
        .replace(/_$/, '')
    )).toLowerCase();
}

/**
 * Turns a single-level object into a ready-to-use query string.
 *
 * @param params - Pre-sanitized key/value pairs
 * @returns A `?key=value` style string
 */
export function toQuery(params: object) {
  const invalid = typeof params !== 'object' || !params || Object.keys(params).length === 0;
  return invalid ? '' : (
    // prettier-ignore
    Object.entries(params).reduce((acc, [key, val], idx) => {
      const valid = typeof val === 'string' || typeof val === 'number';
      return valid ? acc.concat(val ? `${idx === 0 ? '' : '&'}${key}=${val}` : '') : acc;
    }, '?')
  );
}

/**
 * Produces a possessive version of a name using the correct apostrophe rule.
 *
 * @param name - Name to display
 * @returns Label with `'` or `'s`
 */
export function toPossessive(name: string) {
  const invalid = typeof name !== 'string' || !name.length;
  return invalid ? '' : name.concat(/[sxz]$/i.test(name) ? "'" : "'s");
}

/**
 * Converts a number into a display-ready ordinal (1st, 2nd, 3rd, ...).
 *
 * @param number - Numeric position
 * @returns Ordinalized string
 */
export function toOrdinal(number: number): string {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const remainder = number % 100;
  const suffix = suffixes[(remainder - 20) % 10] || suffixes[remainder] || suffixes[0];
  return number + suffix;
}

/**
 * Presents machine-friendly strings as properly spaced, title-cased labels.
 *
 * @param str - The raw string
 * @returns A human-readable label
 */
export function toDisplay(str: string): string {
  return str.replace(/[^\w\d\s]|_/g, ' ').replace(/\b\w/g, (match) => match.toUpperCase());
}

/**
 * Converts identifiers to `camelCase` for API compatibility.
 *
 * @param str - Source value
 * @returns Camel-cased string
 */
export function toCamel(str: string) {
  // prettier-ignore
  return (typeof str !== 'string' && typeof str !== 'number') || !str || !str.length ?
    ''
    : str.replace(/[\s]+/g, ' ').trim()[0].toLowerCase() +
    str
      .replace(/[\s]+/g, ' ')
      .trim()
      .slice(1)
      .replace(/[_-\s]./g, (match) => match[1].toUpperCase());
}

/**
 * Creates a predicate helper for filtering arrays by value.
 *
 * @param predicate - Value that should match
 * @returns Function returning `true` when the candidate equals the predicate
 */
export function whereEqual<T extends Array<string>>(predicate: ArrayElement<T>) {
  return (value: ArrayElement<T>) => value === predicate;
}

/**
 * Produces a sorter that orders collection items by a numeric property.
 *
 * @param key - Property to compare
 * @returns Comparator compatible with `Array.prototype.sort`
 */
export function sortByValue<A, B>(key: string) {
  return (a: A, b: B) => Number(b?.[key as keyof B] || 0) - Number(a?.[key as keyof A] || 0);
}

/**
 * Sorts entries by a date field, supporting nested Contentful-style structures.
 *
 * @param field - Field name containing an ISO date
 * @returns Comparator yielding ascending chronological order
 */
export function sortByDateField<A, B>(field: string) {
  return (a: A, b: B) =>
    new Date(Object(a)?.fields?.[field] || Object(a)?.[field] || 0).getTime() -
    new Date(Object(b)?.fields?.[field] || Object(b)?.[field] || 0).getTime();
}

/**
 * Resolves a list of promises while swallowing individual rejections.
 *
 * @param promises - Items to await
 * @returns Array of fulfilled values or `null` placeholders
 */
export async function resolveSettled<T>(promises: Array<unknown>) {
  (await Promise.allSettled(promises)).map((promise) =>
    promise.status === 'fulfilled' ? (promise.value as T) : null,
  ) as unknown as T;
}

/**
 * Removes entries with nullable `.value` fields from arrays commonly built by forms.
 *
 * @param entries - Array-like structure to sanitize
 * @returns Clean array or empty list
 */
export function clearArray<T = Array<unknown>>(entries: T): Required<T> | [] {
  if (!Array.isArray(entries)) {
    return [];
  }
  return entries.filter((e) => !isNullable(e?.value)) as unknown as Required<T>;
}

/**
 * Produces a deep-cloned object without `undefined` properties.
 *
 * @param obj - Source object
 * @returns Stripped object suitable for API payloads
 */
export function clearObject(obj: object) {
  const newObj = Object.create(null);
  Object.keys(obj).forEach((key) => {
    const result = Object.assign(Object.create(null), obj);
    if (result[key] === Object(result[key])) {
      return (newObj[key] = clearObject(result[key]));
    } else if (result[key] !== undefined) {
      newObj[key] = result[key];
    }
  });
  return newObj;
}

/**
 * Extracts required query parameters from a URL-like string.
 *
 * @param url - String or `URL` instance to inspect
 * @returns Object containing required params (blank when absent)
 */
export function getQueryParams(url: unknown) {
  const defaultParams = REQUIRED_QUERY_PARAMS.reduce(
    (params, param) => ({ ...params, [param]: '' }),
    {},
  );
  if (isNullable(url)) {
    return defaultParams;
  }
  const base = url instanceof URL ? url.href : String(url);
  const index = base.indexOf('?');

  const params = base
    .substring(index >= 0 ? index + 1 : 0)
    .split('&')
    .filter((item) => item !== '')
    .reduce((acc, current) => {
      const arr = current.split('=');
      acc[arr[0]] = arr[1] ?? '';
      return acc;
    }, Object.create(null));

  if (Object.values(params).length < REQUIRED_QUERY_PARAMS.length) {
    const diff = REQUIRED_QUERY_PARAMS.filter(
      (param) => !Object.keys(toQuery(params).includes(param)),
    );
    logger.warn(`Expected at least ${diff} more query parameter(s)!`);
  }

  return { ...defaultParams, ...params };
}

/**
 * Breaks a dataset into page-sized chunks for pagination controls.
 *
 * @param array - Items to distribute
 * @param itemsPerPage - Max items per chunk
 * @returns A paginated matrix
 */
export function paginateBy<T = Record<string, string>>(array: T[], itemsPerPage: number): T[][] {
  return array.reduce(
    (acc, item) => {
      const hasItems = acc[acc.length - 1].length !== 0;
      const isFull = acc[acc.length - 1].length % itemsPerPage === 0;
      return hasItems && isFull ? acc.concat([item]) : acc[acc.length - 1].concat(item);
    },
    [Array(0)],
  );
}

/**
 * Provides a quick shuffle for single-use randomization (non-cryptographic).
 *
 * @param array - Items to reorder in-place
 * @returns The same array reference with elements shuffled
 */
export function shuffle(array: Array<unknown>) {
  return array.sort(() => 0.5 - Math.random());
}

/**
 * Randomizes question options while honoring fixed display positions.
 *
 * @param options - Set of answer options with optional `fixedPosition`
 * @returns Array ready for rendering in UI components
 */
export function getRandomOptions(options: Record<string, unknown>[]) {
  const optionsToShuffle = [...options].filter((option) => !option?.fixedPosition);

  const fixedPositions = [...options].reduce((acc, option, index) => {
    if (!option.fixedPosition) return acc;
    acc.push({ option, index });
    return acc;
  }, Array(0));

  const newArray = Array(options.length).fill(undefined, 0, options.length);

  const randomOptions = shuffle(optionsToShuffle);

  const questionOptions = newArray.map((_, index) => {
    const fixedPositionFound = fixedPositions.find((fixed) => fixed.index === index);
    if (fixedPositionFound) {
      return fixedPositionFound.option;
    }
    return randomOptions.pop();
  });

  return questionOptions;
}

// prettier-ignore
export const logger =
  (
    process.env.NODE_ENV === 'test' ||
    process.env.ENVIRONMENT === 'test' ||
    typeof window === 'undefined'
  ) ?
    {
      info: () => undefined,
      warn: () => undefined,
      error: () => undefined,
    }
    : createLogger();

export function createApiError(status: number, message: string, setup = () => undefined) {
  setup();
  throw Error(`${status} - ${message}`);
}

export function handleApiError(error: unknown) {
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof error.response === 'object' &&
    typeof Object(error.response).data === 'object' &&
    Object(error.response)?.data?.message !== undefined &&
    Object(error.response)?.data.message !== null
  ) {
    const data = Object(error.response).data;
    const message = `Salesforce Error - ${data.message}! ${data.additionalErrors?.map(({ message }: { message: string }) => message).join('\n')}`;
    logger.error(message);
    return { message };
  } else {
    const _error = Object(error ?? {});
    logger.error(error);
    const errorMessage =
      typeof _error?.message === 'string' ? _error.message : JSON.stringify(error ?? {});

    // prettier-ignore
    const status = errorMessage.indexOf('-') === 4 ? errorMessage.split(' - ')[0] : StatusCodes.INTERNAL_SERVER_ERROR;
    const message = errorMessage.indexOf('-') === 4 ? errorMessage.split(' - ')[1] : errorMessage;

    return { status, message };
  }
}

export function requestLogger(requestName: string) {
  return (request: InternalAxiosRequestConfig) => {
    const path = `${request.url?.replace(request.baseURL ?? '', '')}`;
    const params = request.params ? `\n Query: ${toQuery(request.params)}` : '';
    logger.info(
      decodeURIComponent(
        `${(request.method ?? 'N/A').padStart(4, ' ').toUpperCase()} ${path}${params} (${requestName}) `,
      ),
    );
    return request;
  };
}

export function withError<T = Api.Response>(fetchFunction: () => Promise<T>) {
  return async () => {
    try {
      return await fetchFunction();
    } catch (error) {
      const { message } = handleApiError(error) ?? {};
      return {
        props: {
          hasError: true,
          error: message,
        },
      };
    }
  };
}

/**
 * Creates a wrapper around localStorage to prevent errors
 * that could happen when window is not available or the user
 * has cookies/storage disabled on their browser.
 *
 * This interface is taking care of the serialization/deserialization
 * so there is no need to do it manually with the `JSON` utilities (`stringify`, `parse`).
 *
 * If the browser does not support localStorage, the interface will return a dummy object
 * populating the fallback methods with empty functions. In this case, no error will be thrown
 * and the application will continue to work as expected but `null` will be returned.
 */
const Storage = () => {
  try {
    if (
      typeof window !== 'undefined' &&
      'localStorage' in window &&
      'getItem' in localStorage &&
      'setItem' in localStorage &&
      'removeItem' in localStorage &&
      typeof localStorage.getItem === 'function' &&
      typeof localStorage.setItem === 'function' &&
      typeof localStorage.removeItem === 'function'
    ) {
      // prettier-ignore
      return {
        /**
         * Retrieves an item from `localStorage` and parses it if it's available.
         *
         * @param key - The key of the item to be retrieved
         * @example
         * Storage.setItem('foo', 'bar')
         * console.log(Stroage.getItem('foo')) // 'bar'
         */
        getItem: (key: string) => {
          const storedValue = window.localStorage.getItem(key);
          return storedValue ? JSON.parse(storedValue) : null;
        },
        /**
         * Serializes an item and place it in the `localStorage`.
         *
         * @param key - The key of the item to be set
         * @param value - The value of the item to be set
         * @example
         * Storage.setItem('foo', 'bar')
         * console.log(Storage.getItem('foo')) // 'bar'
         */
        setItem: (key: string, value: unknown) => window.localStorage.setItem(key, JSON.stringify(value)),
        /**
         * Removes an item from `localStorage` by the given key.
         *
         * @param key - The key of the item to be removed
         * @example
         * Storage.setItem('foo', 'bar')
         * console.log(Stroage.getItem('foo')) // 'bar'
         * Storage.removeItem('foo')
         * console.log(Stroage.getItem('foo')) // null
         */
        removeItem: (key: string) => window.localStorage.removeItem(key),
      }
    } else {
      throw new TypeError('Storage is not available!');
    }
  } catch (error) {
    logger.error('Failed to execute storage function!', error);
    return {
      getItem: () => null,
      setItem: () => undefined,
      removeItem: () => undefined,
    };
  }
};

export const storage = Storage();
